import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { GetPatientReportsById } from '../../../apollo-client/api-query';
import { refetchPatient, setPatientReports } from '../../../store';
import { useLazyQuery } from '@apollo/client';
import { DEMO_REPORTS } from '../../SectionHealthReport';

const PatientLayout = ({ children }) => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const isRefetchPatient = useSelector(state => state.isRefetchPatient)

  // errorPolicy: 'all' — roles without ViewReports permission (e.g. Sales) get
  // a nulled `node` from this query. With 'all' we still see whatever partial
  // data exists; without it, Apollo treats the whole response as failed.
  // suppressToast — field-level permission errors are expected for Sales; no toast.
  const [getReports] = useLazyQuery(GetPatientReportsById, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    context: { suppressToast: true },
  })

  const fetchData = useCallback(async () => {
    const result = await getReports({
      variables: {
        // locked: false,
        id: id,
        // reportTypeId: "ReportType:5"
      }
    })

    const reportsByType = result?.data?.node?.reportsByType;

    // If the user can't read reports (e.g. Sales lacks ViewReports), the node
    // resolver nulls out. Treat this as "no reports to display" — don't log
    // the user out, don't crash, just dispatch an empty report list so the
    // patient detail page renders without the reports section.
    if (!reportsByType) {
      dispatch(setPatientReports({ data: [], loading: false }));
      return;
    }

    //kits data
    const kitsData = result?.data?.node?.kits?.map(kit => kit.id) || [];
    const reportsData = reportsByType.filter(itemData => {
        const item = itemData?.report;
        const reportKitId = item?.kit?.id;
        return kitsData.includes(reportKitId)
      }).map((itemData => {
        const item = itemData?.report;
        const data = item?.data ? JSON.parse(item?.data) : null;
        let priority = 0
        
        if (data) {
          let dataUpdatedAt = moment(item.dataUpdatedAt).format('MMMM Do YYYY')
          let updatedAtOrg = item.dataUpdatedAt
          if (item.reportType.name !== 'Medications' && item.reportType.name !== 'Nutrition') {
            dataUpdatedAt = moment(data.report_approval_date).format('MMMM Do YYYY')
            updatedAtOrg = data.report_approval_date
          }
          if (data?.report_sections && data?.report_sections.length > 0 && (item.reportType.name !== 'Medications' && item.reportType.name !== 'Nutrition')) {
            data?.report_sections.forEach(report => {
              if(Array.isArray(report.variants)) {
                priority += report.variants?.length || 0
              } else if(report.alzheimer_report) {
                //priority += 1;
              } else if(report?.variants?.snv || report?.variants?.sv) {
                priority += report?.variants?.snv?.length + report?.variants?.sv?.length || 0
              }
            })
          }
          if (item?.reportType?.name === 'Medications' && data?.medications) {
            data?.medications.forEach(medication => {
              if (parseInt(medication.guidanceLevel) === 3) priority ++
            })
          }
          return {
            id: item?.id,
            version: item?.version ?? 0,
            name: DEMO_REPORTS.find(data => data.value === item.reportType.name)?.name,
            value: item.reportType.name,
            date: moment(item?.createdAt).format('MM/DD/YYYY'),
            dataUpdatedAt,
            updatedAtOrg: updatedAtOrg,
            locked: item?.locked,
            needsReview: item?.needsReview,
            priority: priority,
            color: DEMO_REPORTS.find(data => data?.value === item?.reportType?.name)?.color || 'primary.darkGreen',
            data,
            icon: DEMO_REPORTS.find(data => data?.value === item?.reportType?.name)?.icon,
            link: DEMO_REPORTS.find(data => data?.value === item?.reportType?.name)?.link,
            pdfUrl: item?.pdfUrl
          }
        }
        
        return null
      })).filter(item => item)

    dispatch(setPatientReports({
      data: reportsData.sort((a, b) => a.name.localeCompare(b.name)),
      loading: false
    }))
  }, [id, getReports, dispatch])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useMemo(() => {
    if (isRefetchPatient) {
      fetchData();
      dispatch(refetchPatient(false))
    }
  }, [isRefetchPatient, dispatch, fetchData])

  return (
    <>{children}</>
  )
}

export default PatientLayout;
